import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, BrainCircuit, Database, ShieldCheck, Waves } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { listAppExposeBindings } from '../api';
import { getDefaultTenantId } from '@/lib/cpClient';
import { useAuthStore } from '@/store/authStore';
import { resolveExposeExecuteTenant } from '../../api-manager/exposeTenantHonesty';

const SOURCE_LABELS: Record<string, string> = {
  memory: 'Memory',
  iot_telemetry: 'IoT Telemetry',
  iot_devices: 'IoT Devices',
  api_pipeline: 'Can read data from its API feeds',
  pim: 'PIM',
  dam: 'DAM',
  entity: 'Identity & Entities',
  weather: 'Weather',
};

function labelForSource(sourceType: string, sourceId?: string | null) {
  const base = SOURCE_LABELS[sourceType] || sourceType;
  return sourceId ? `${base}: ${sourceId}` : base;
}

export function AppDataSourcesSummaryCard({
  appId,
  dataSettings,
}: {
  appId: string;
  dataSettings?: {
    storage_mode?: 'shared' | 'dedicated';
    database?: string | null;
  } | null;
}) {
  const navigate = useNavigate();
  const selectedTenant = useAuthStore((s) => s.selectedTenant);
  const sessionTenant = resolveExposeExecuteTenant({
    selectedTenant,
    hostTenant: getDefaultTenantId(),
  });
  const bindingsQuery = useQuery({
    queryKey: ['app-data-sources-summary', appId, sessionTenant],
    queryFn: async () => {
      const response = await listAppExposeBindings(appId, sessionTenant);
      return response.bindings || [];
    },
    enabled: Boolean(appId),
    staleTime: 30_000,
  });

  const bindings = bindingsQuery.data || [];
  const enabledBindings = bindings.filter((binding: any) => binding.enabled);
  const sourceSummary = useMemo(() => {
    const readWrite = enabledBindings.filter((binding: any) => (binding.scopes || []).includes('write')).length;
    const memoryReady = enabledBindings.some((binding: any) => binding.source_type === 'memory');
    return {
      enabled: enabledBindings.length,
      readWrite,
      memoryReady,
    };
  }, [enabledBindings]);

  const storageMode = String(dataSettings?.storage_mode || 'shared').toLowerCase();
  const storageLabel = storageMode === 'dedicated' ? 'Dedicated MongoDB' : 'Platform-managed storage';
  const databaseName = dataSettings?.database || (storageMode === 'dedicated' ? 'Dedicated database configured' : 'Shared platform data plane');

  return (
    <Card className="mt-4 border-slate-200 shadow-sm overflow-hidden">
      <div className="bg-slate-50 border-b border-slate-200 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-slate-800">
              <ShieldCheck className="w-5 h-5 text-indigo-600" />
              <h3 className="font-semibold text-sm">Runtime Data Access</h3>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              App Instances use direct Mongo for heavy CRUD and API Expose for governed runtime surfaces like Memory, IoT, DAM, and PIM.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => navigate(`/api-expose?tab=data-sources&app_id=${encodeURIComponent(appId)}`)}
          >
            Open API Expose
            <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </div>

      <CardContent className="p-4 space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-2 text-slate-700">
              <Database className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-semibold uppercase tracking-wide">Storage</span>
            </div>
            <div className="mt-2 text-sm font-semibold text-slate-900">{storageLabel}</div>
            <div className="mt-1 text-xs text-slate-500">{databaseName}</div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-2 text-slate-700">
              <Waves className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-semibold uppercase tracking-wide">Exposed Sources</span>
            </div>
            <div className="mt-2 text-2xl font-semibold text-slate-900">{sourceSummary.enabled}</div>
            <div className="mt-1 text-xs text-slate-500">
              {sourceSummary.readWrite} with write access
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-2 text-slate-700">
              <BrainCircuit className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-semibold uppercase tracking-wide">Living Memory</span>
            </div>
            <div className="mt-2 text-sm font-semibold text-slate-900">
              {sourceSummary.memoryReady ? 'Enabled for this app' : 'Not enabled yet'}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Enable this to unlock `public/memories` plus MCP memory guidance.
            </div>
          </div>
        </div>

        {bindingsQuery.isLoading ? (
          <div className="text-xs text-slate-500">Loading data-source policy...</div>
        ) : enabledBindings.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
            No governed runtime sources are enabled yet. Use API Expose to turn on Memory, IoT, DAM, PIM, or other public surfaces for this app.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Enabled bindings</div>
            <div className="flex flex-wrap gap-2">
              {enabledBindings.map((binding: any) => (
                <Badge key={binding.binding_id} variant="secondary" className="bg-slate-100 text-slate-700">
                  {labelForSource(binding.source_type, binding.source_id)}
                  {(binding.scopes || []).length > 0 ? ` · ${(binding.scopes || []).join('/')}` : ''}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
